import { assumptionsSchema, freshNodeDisplay, freshPptxGeom, fxRateSchema, severanceMonthsSchema } from '@integrate/contracts';
import { nodeDisplayFromMeta, pptxGeomFromMeta } from '../import.js';
import { CommandError } from '../errors.js';
import { required } from './types.js';
/* ------------------------------------------------------------------ *
 * Assumptions
 * ------------------------------------------------------------------ */
/**
 * What kind of thing each assumption key is — the exhaustiveness guard this handler never had.
 *
 * `GRID_KEYS` used to be a hand-written two-member set beside a scalar branch that coerced with
 * `Number()`. A key added to `ASSUMPTION_KEYS` and forgotten here fell into the scalar branch
 * silently — for a grid that means `assumptions.fxRates = Number(value)`, replacing the map with a
 * number, hidden from the compiler by a cast. `satisfies Record<AssumptionKey, …>` makes a
 * forgotten key a build error naming it, the same shape `COMMAND_REACH` and the redactor's
 * `FIELD_POLICY` use.
 *
 * `label` is the third kind: a string the arithmetic never reads. The numeric-scalar branch refuses
 * it by construction (its `Number()` guard), so it needs a branch of its own.
 */
const ASSUMPTION_KIND = {
    loadedCostMultiplier: 'scalar',
    severanceMonthsDefault: 'scalar',
    hiringCostPct: 'scalar',
    retentionPct: 'scalar',
    severanceByCountry: 'grid',
    severanceBySeniority: 'grid',
    fxRates: 'grid',
    consultationMonthsByCountry: 'grid',
    loadedCostMultiplierByCountry: 'grid',
    closeMonthByCountry: 'grid',
    reportingCurrency: 'label',
    day1Date: 'label',
};
const LABEL_KEYS = new Set(Object.keys(ASSUMPTION_KIND).filter((k) => ASSUMPTION_KIND[k] === 'label'));
/**
 * What each label means, in its own words. Blank is a legal state for every label — clearing one
 * must be as undoable as setting it — which is exactly what the numeric-scalar branch refuses.
 */
const LABEL_TEXT = {
    reportingCurrency: {
        accept(raw) {
            if (!assumptionsSchema.shape.reportingCurrency.safeParse(raw).success) {
                throw new CommandError('invalid', 'The reporting currency is a short code, like EUR or USD');
            }
            return raw;
        },
        set: (value) => `Reporting currency → ${value}`,
        cleared: 'Reporting currency → unnamed',
    },
    day1Date: {
        accept(raw) {
            // The regex is the shape; the round-trip catches 2026-02-31. Date.UTC is deterministic.
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
            const valid = m !== null &&
                (() => {
                    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
                    return (d.getUTCFullYear() === Number(m[1]) &&
                        d.getUTCMonth() === Number(m[2]) - 1 &&
                        d.getUTCDate() === Number(m[3]));
                })();
            if (!valid) {
                throw new CommandError('invalid', 'Day 1 is a date, like 2026-09-01 — or blank to keep the index-only clock');
            }
            return raw;
        },
        set: (value) => `Day 1 → ${value}`,
        cleared: 'Day 1 → undated',
    },
};
const GRID_KEYS = new Set(Object.keys(ASSUMPTION_KIND).filter((k) => ASSUMPTION_KIND[k] === 'grid'));
/**
 * What each grid's tiers mean, in its own words.
 *
 * The mechanics are shared — sub-key rows, blank removes the tier, `before` captured as absence —
 * but the words are not: a severance tier inherits down a chain, an FX tier falls back to × 1, and
 * an error telling somebody an FX rate "must be a number of months" would send them to the wrong
 * mental model. The severance strings are kept verbatim from before this table existed.
 */
const GRID_TEXT = {
    severanceByCountry: severanceGridText('country'),
    severanceBySeniority: severanceGridText('seniority'),
    consultationMonthsByCountry: consultationGridText(),
    closeMonthByCountry: {
        needs: 'A close-month tier needs a country to apply to',
        assertValue(value, key) {
            // Whole months only — the clock is integer months, and a half-month close would put a step
            // between two bars. Zero refused, not stored: Day 1 is what an absent tier already means,
            // and blank-for-identity must not have a numeric synonym (the FX-grid reasoning).
            if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
                throw new CommandError('invalid', `Close month for country "${key}" must be a whole number of months after Day 1, or blank for Day 1`);
            }
            if (value === 0) {
                throw new CommandError('invalid', `Close month for country "${key}": 0 is Day 1 — clear the tier instead`);
            }
            // M119 is the latest close whose accrual step (at k+1) still fits on the 120-month axis.
            if (value > 119) {
                throw new CommandError('invalid', `Close month for country "${key}" must be M119 or earlier — the month axis ends at M120`);
            }
        },
        set: (key, value) => `Close month for country "${key}" → M${value}`,
        cleared: (key) => `Close month for country "${key}" → Day 1`,
    },
    loadedCostMultiplierByCountry: {
        needs: 'A loaded-cost tier needs a country to apply to',
        assertValue(value, key) {
            // Zero would zero every loaded figure in that country — the global multiplier's own guard.
            if (!Number.isFinite(value) || value <= 0) {
                throw new CommandError('invalid', `Loaded-cost multiplier for country "${key}" must be greater than zero, or blank for the default`);
            }
            assertInRange(assumptionsSchema.shape.loadedCostMultiplier, value, `Loaded-cost multiplier for country "${key}"`);
        },
        set: (key, value) => `Loaded-cost multiplier for country "${key}" → ${value}×`,
        cleared: (key) => `Loaded-cost multiplier for country "${key}" → default`,
    },
    fxRates: {
        needs: 'An FX tier needs a currency code to apply to',
        assertValue(value, key) {
            // A zero rate zeroes every salary it touches — the loadedCostMultiplier reasoning, applied to
            // a grid. Blank-for-identity is the way to say "no conversion"; zero must not be a synonym.
            if (!Number.isFinite(value) || value <= 0) {
                throw new CommandError('invalid', `FX rate "${key}" must be a positive number (the rate into the reporting currency), or blank for × 1`);
            }
            assertInRange(fxRateSchema, value, `FX rate "${key}"`);
        },
        set: (key, value) => `FX rate "${key}" → ${value}`,
        cleared: (key) => `FX rate "${key}" → × 1`,
    },
};
function consultationGridText() {
    return {
        needs: 'A consultation tier needs a country to apply to',
        assertValue(value, key) {
            if (!Number.isFinite(value) || value < 0) {
                throw new CommandError('invalid', `Consultation lead time for country "${key}" must be a number of months, or blank for none`);
            }
            assertInRange(severanceMonthsSchema, value, `Consultation lead time for country "${key}"`);
        },
        set: (key, value) => `Consultation lead time for country "${key}" → ${value} months`,
        cleared: (key) => `Consultation lead time for country "${key}" → none`,
    };
}
function severanceGridText(noun) {
    return {
        needs: 'A severance tier needs a country or grade to apply to',
        assertValue(value, key) {
            if (!Number.isFinite(value) || value < 0) {
                throw new CommandError('invalid', `Severance for ${noun} "${key}" must be a number of months, or blank to inherit`);
            }
            assertInRange(severanceMonthsSchema, value, `Severance for ${noun} "${key}"`);
        },
        set: (key, value) => `Severance for ${noun} "${key}" → ${value} months`,
        cleared: (key) => `Severance for ${noun} "${key}" → inherit`,
    };
}
/** Screened everywhere else in the codebase (`contracts/src/domain.ts:94`); a grid key is no different. */
const FORBIDDEN_SUBKEYS = new Set(['__proto__', 'constructor', 'prototype']);
/**
 * Hold one written value to the range its own schema states.
 *
 * The VALUE rather than the whole assumption set, deliberately. `assumptionsSchema` states ranges a
 * client may re-display (contracts/src/domain.ts:144-153) and until now parsed nothing, so the API
 * accepted a 5,000-month severance default no UI built to the contract could show back. But the
 * importer does not enforce those ranges — `mergeAssumptions` coerces with `numberOr` and no bound —
 * so checking the whole object here would let one out-of-range value in an imported file block every
 * unrelated assumption edit afterwards, with an error naming the wrong field.
 */
function assertInRange(schema, value, what) {
    if (!schema.safeParse(value).success) {
        throw new CommandError('invalid', `${what} is out of range`);
    }
}
/** A grid tier the engine could never read back. */
function assertReadableSubKey(key, needs) {
    if (key.trim() === '') {
        throw new CommandError('invalid', needs);
    }
    if (FORBIDDEN_SUBKEYS.has(key)) {
        throw new CommandError('invalid', `"${key}" is a reserved identifier and cannot name a tier`);
    }
}
export const setAssumption = {
    needs: ['project'],
    apply(slice, command) {
        const project = required(slice, 'project');
        const assumptions = {
            ...project.assumptions,
            // Null-prototype, so a grid can never be reached through an inherited key: these are keyed by the
            // client's own country, grade and currency strings.
            severanceByCountry: Object.assign(Object.create(null), project.assumptions.severanceByCountry),
            severanceBySeniority: Object.assign(Object.create(null), project.assumptions.severanceBySeniority),
            fxRates: Object.assign(Object.create(null), project.assumptions.fxRates),
            consultationMonthsByCountry: Object.assign(Object.create(null), project.assumptions.consultationMonthsByCountry),
            loadedCostMultiplierByCountry: Object.assign(Object.create(null), project.assumptions.loadedCostMultiplierByCountry),
            closeMonthByCountry: Object.assign(Object.create(null), project.assumptions.closeMonthByCountry),
        };
        const isGrid = GRID_KEYS.has(command.key);
        if (isGrid !== (command.subKey !== null)) {
            throw new CommandError('invalid', isGrid
                ? `${command.key} is a grid and needs a subKey (the country or grade)`
                : `${command.key} takes no subKey`);
        }
        let before;
        let summary;
        if (isGrid) {
            const gridKey = command.key;
            const text = GRID_TEXT[gridKey];
            const grid = assumptions[gridKey];
            const key = command.subKey;
            assertReadableSubKey(key, text.needs);
            /*
              `before` is captured as an ABSENCE, not as a zero, and normalised to one encoding.
      
              This capture is the whole reason a first-time override can be undone: `?? 0` here would make undo
              write a stored zero — a real override that stops the inheritance chain — where the tier had
              previously not existed at all. An imported `''` is the same trap from the other side, so it is
              recorded as null too and the inverse REMOVES the tier rather than restoring an encoding no
              writer produces.
            */
            const stored = Object.prototype.hasOwnProperty.call(grid, key) ? grid[key] : null;
            before = stored === null || String(stored).trim() === '' ? null : stored;
            // Blank means inherit, and it arrives as null from a caller that means it or as '' from an input
            // box. Number('') is 0, so coercing here would silently price a whole country at no severance.
            const blank = command.value === null || String(command.value).trim() === '';
            if (blank) {
                // Removal, not zero. The tier stops existing and its fallback resumes — inheritance for
                // severance, × 1 for an FX rate.
                delete grid[key];
                summary = text.cleared(key);
            }
            else {
                const value = Number(command.value);
                text.assertValue(value, key);
                grid[key] = value;
                summary = text.set(key, value);
            }
        }
        else if (LABEL_KEYS.has(command.key)) {
            /*
             * The strings in the vocabulary. The numeric-scalar branch below refuses anything that does
             * not coerce to a number, which is correct for every arithmetic input and wrong for a label —
             * and unlike those scalars, blank here is a legal state, not a refusal: clearing a label must
             * be as undoable as setting it.
             */
            const labelKey = command.key;
            const text = LABEL_TEXT[labelKey];
            before = assumptions[labelKey];
            const raw = command.value === null ? '' : String(command.value).trim();
            if (raw === '') {
                assumptions[labelKey] = null;
                summary = text.cleared;
            }
            else {
                const accepted = text.accept(raw);
                assumptions[labelKey] = accepted;
                summary = text.set(accepted);
            }
        }
        else {
            const key = command.key;
            before = assumptions[key];
            const value = Number(command.value);
            // Deliberately NOT the input-box rule: '' coerces to 0, and a scalar assumption of zero is a real
            // decision (no severance at all) that must be typed rather than fallen into.
            if (command.value === null || String(command.value).trim() === '' || !Number.isFinite(value) || value < 0) {
                throw new CommandError('invalid', `${command.key} must be a number of at least zero`);
            }
            if (command.key === 'loadedCostMultiplier' && value <= 0) {
                throw new CommandError('invalid', 'The loaded-cost multiplier must be greater than zero');
            }
            assertInRange(assumptionsSchema.shape[key], value, command.key);
            assumptions[key] = value;
            summary = describeScalar(command.key, value);
        }
        return {
            writes: [{ t: 'project', patch: { assumptions } }],
            inverse: [
                {
                    type: 'setAssumption',
                    key: command.key,
                    subKey: command.subKey,
                    value: before,
                },
            ],
            summary,
            touched: [`assumption:${command.key}${command.subKey ? `:${command.subKey}` : ''}`],
        };
    },
};
function describeScalar(key, value) {
    switch (key) {
        case 'loadedCostMultiplier':
            return `Loaded-cost multiplier → ${value}×`;
        case 'severanceMonthsDefault':
            return `Default severance → ${value} months`;
        case 'hiringCostPct':
            return `Hiring cost → ${value}% of salary`;
        case 'retentionPct':
            return `Retention budget → ${value}% of salary`;
        default:
            return `${key} → ${value}`;
    }
}
/* ------------------------------------------------------------------ *
 * Saved views
 * ------------------------------------------------------------------ */
/**
 * A reporting-line view needs a reporting line to draw.
 *
 * `buildReportingHierarchy` reads each person's supervisor through `getEmpField`, so with no mapped
 * supervisor column every lookup returns `''`, nobody resolves to a manager, and every person parents
 * straight to the root — a flat fan of hundreds of cards that reads as "this company has no managers"
 * rather than as "we do not have that data". The shipped app refuses at both ends (`orgchart.html:3316`
 * and `:10529`); refusing here is the same decision, said out loud, and it is the shape
 * `setColumnMapping` already uses for a level with no column.
 *
 * Deliberately NOT pointed at the app's own hint (`:1699`), which names a path that cannot re-map
 * anything: on the platform the fix is `setColumnMapping` followed by `rebuildRosterFromSource`.
 */
function assertLayoutIsDrawable(layout, columnMap) {
    if (layout !== 'reporting')
        return;
    if (columnMap.supervisorId)
        return;
    throw new CommandError('conflict', 'The reporting-line layout needs a mapped Supervisor ID column — map one and re-derive the roster from source first');
}
export const saveView = {
    needs: ['views', 'settings'],
    apply(slice, command) {
        const views = required(slice, 'views');
        if (views[command.viewId]) {
            throw new CommandError('conflict', `A view with id ${command.viewId} already exists`);
        }
        assertLayoutIsDrawable(command.viewLayout, required(slice, 'settings').columnMap);
        const row = {
            name: command.name,
            viewGrouping: command.viewGrouping,
            filters: command.filters,
            viewLayout: command.viewLayout,
        };
        // The first view a project saves becomes its default, matching the original (:3038): a project with
        // exactly one saved view and no default would open on the raw hierarchy, which is never what the
        // person who just saved it wanted.
        const isFirst = Object.keys(views).length === 0;
        return {
            writes: [
                { t: 'view', id: command.viewId, row },
                ...(isFirst ? [{ t: 'viewDefault', viewId: command.viewId }] : []),
            ],
            inverse: [{ type: 'deleteView', viewId: command.viewId }],
            summary: `Saved view "${command.name}"${isFirst ? ' (now the default)' : ''}`,
            touched: [`view:${command.viewId}`],
        };
    },
};
export const updateView = {
    needs: ['views', 'settings'],
    apply(slice, command) {
        const existing = required(slice, 'views')[command.viewId];
        if (!existing)
            throw new CommandError('not_found', `No such view: ${command.viewId}`);
        if (command.patch.viewLayout) {
            assertLayoutIsDrawable(command.patch.viewLayout, required(slice, 'settings').columnMap);
        }
        const next = { ...existing, ...command.patch };
        const before = {};
        for (const key of Object.keys(command.patch)) {
            before[key] = existing[key];
        }
        return {
            writes: [
                {
                    t: 'view',
                    id: command.viewId,
                    row: {
                        name: next.name,
                        viewGrouping: next.viewGrouping,
                        filters: next.filters,
                        viewLayout: next.viewLayout,
                    },
                },
            ],
            inverse: [{ type: 'updateView', viewId: command.viewId, patch: before }],
            summary: `Updated view "${existing.name}"`,
            touched: [`view:${command.viewId}`],
        };
    },
};
export const deleteView = {
    needs: ['views'],
    apply(slice, command) {
        const views = required(slice, 'views');
        const existing = views[command.viewId];
        if (!existing)
            throw new CommandError('not_found', `No such view: ${command.viewId}`);
        /*
          Which view the project opens on, stated rather than inferred.
    
          `saveView` decides for itself whether the view it is creating becomes the default, from the view
          count AT APPLY TIME (`:138`). That is right for a first save and wrong for an undo, because an
          inverse re-executes as a command against present state: delete the project's only view when it was
          NOT the default, revert that delete, and `saveView` finds an empty project, concludes this must be
          the first view, and installs it as the default. Every user now opens on a view nobody chose, with
          no event saying the default changed — a revert that produced a state which never existed.
    
          So the inverse says what the default was, unconditionally, including when it was nothing.
        */
        const defaultViewId = Object.entries(views).find(([, v]) => v.isDefault)?.[0] ?? null;
        return {
            writes: [{ t: 'view', id: command.viewId, row: null }],
            inverse: [
                {
                    type: 'saveView',
                    viewId: command.viewId,
                    name: existing.name,
                    viewGrouping: existing.viewGrouping,
                    filters: existing.filters,
                    viewLayout: existing.viewLayout,
                },
                { type: 'setDefaultView', viewId: defaultViewId },
            ],
            summary: `Deleted view "${existing.name}"`,
            // 'view:default' as well, so reverting this delete is refused once somebody has since chosen a
            // different default — the supersede guard would otherwise let the undo silently overwrite it.
            touched: [`view:${command.viewId}`, 'view:default'],
        };
    },
};
export const setDefaultView = {
    needs: ['views'],
    apply(slice, command) {
        const views = required(slice, 'views');
        if (command.viewId !== null && !views[command.viewId]) {
            throw new CommandError('not_found', `No such view: ${command.viewId}`);
        }
        const previous = Object.entries(views).find(([, v]) => v.isDefault)?.[0] ?? null;
        return {
            writes: [{ t: 'viewDefault', viewId: command.viewId }],
            inverse: [{ type: 'setDefaultView', viewId: previous }],
            summary: command.viewId === null
                ? 'The project now opens on the canonical hierarchy'
                : `The project now opens on view "${views[command.viewId].name}"`,
            touched: ['view:default'],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Display configuration
 * ------------------------------------------------------------------ */
/*
  "Nothing stored" is not "nothing to restore".

  Both handlers used to emit `inverse: before ? [...] : null`, reading the raw settings column — which
  defaults to `{}` and is only written on import when the file carried one. So on the overwhelmingly
  common project the first display change recorded either `setNodeDisplay({})`, a command that fails
  its own schema and survives only because a revert does not re-parse, or no inverse at all — and a
  null inverse makes `revert` refuse the WHOLE batch, telling the user to restore the whole project from
  a point in time to recover a checkbox.

  A project with no stored config is a project running on the defaults, which is a perfectly
  restorable state. `nodeDisplayFromMeta` normalises `{}` and a partial alike into a complete object,
  so the inverse always carries something its own schema accepts.
*/
export const setNodeDisplay = {
    needs: ['settings'],
    apply(slice, command) {
        const before = nodeDisplayFromMeta(required(slice, 'settings').nodeDisplay, freshNodeDisplay()) ??
            freshNodeDisplay();
        return {
            writes: [{ t: 'settings', patch: { nodeDisplay: command.nodeDisplay } }],
            // Shared rather than personal, because the deck exporter reads the same config — the promise that
            // a slide shows what the screen shows only holds if there is one answer per project.
            inverse: [{ type: 'setNodeDisplay', nodeDisplay: before }],
            summary: 'Updated what the node cards show',
            touched: ['nodeDisplay'],
        };
    },
};
export const setPptxGeom = {
    needs: ['settings'],
    apply(slice, command) {
        const before = pptxGeomFromMeta(required(slice, 'settings').pptxGeom) ?? freshPptxGeom();
        return {
            writes: [{ t: 'settings', patch: { pptxGeom: command.pptxGeom } }],
            inverse: [{ type: 'setPptxGeom', pptxGeom: before }],
            summary: 'Updated the slide title and content geometry',
            touched: ['pptxGeom'],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Intake configuration
 * ------------------------------------------------------------------ */
export const setColumnMapping = {
    needs: ['settings', 'project'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        // Name and id are what make a person identifiable and a hierarchy connectable; without them the
        // roster is a spreadsheet, not an organisation.
        for (const requiredKey of ['id', 'name']) {
            if (!command.columnMap[requiredKey]) {
                throw new CommandError('invalid', `The ${requiredKey} column must stay mapped`);
            }
        }
        // Two logical fields cannot read the same column: the reverse mapping on export would be ambiguous
        // and one of them would silently win.
        const byColumn = new Map();
        for (const [key, column] of Object.entries(command.columnMap)) {
            const clash = byColumn.get(column);
            if (clash) {
                throw new CommandError('invalid', `"${column}" is mapped to both ${clash} and ${key}`);
            }
            byColumn.set(column, key);
        }
        // A level whose column is unmapped would group everybody into one blank bucket. The original
        // silently drops such levels on confirm (:3534); refusing is the same decision, said out loud.
        const orphaned = required(slice, 'project')
            .groupingLevels.filter((l) => !command.columnMap[l.key])
            .map((l) => l.label);
        if (orphaned.length) {
            throw new CommandError('conflict', `These hierarchy levels would have no column: ${orphaned.join(', ')}. Remove them first.`);
        }
        const changed = Object.keys({ ...settings.columnMap, ...command.columnMap }).filter((k) => settings.columnMap[k] !== command.columnMap[k]);
        return {
            // Deliberately does NOT rebuild the roster. Employee field values are re-derived from the source
            // rows by rebuildRosterFromSource, which says what it destroys.
            writes: [{ t: 'settings', patch: { columnMap: command.columnMap } }],
            inverse: [{ type: 'setColumnMapping', columnMap: settings.columnMap }],
            summary: changed.length
                ? `Re-mapped ${changed.length} column${changed.length === 1 ? '' : 's'}: ${changed.join(', ')}`
                : 'Column mapping unchanged',
            touched: ['columnMap'],
        };
    },
};
/**
 * Rewrite head group ids after the level ladder is reordered.
 *
 * A group id is content-addressed on its resolved level PATH — `G:lvl1=Ops||lvl2=Field` — with the
 * segments in ladder order. Reorder the ladder and the same group regenerates as
 * `G:lvl2=Field||lvl1=Ops`, so every designation below the first level silently stops matching. The app
 * writes the new ladder and nothing else, which is why reordering levels there detaches every head
 * below level 1: nothing errors, the heads are simply gone from the chart.
 *
 * Only a pure REORDER is repaired, deliberately. Removing a level genuinely merges identities — both
 * `G:lvl1=A||lvl2=B` and `G:lvl1=A||lvl2=C` become `G:lvl1=A` — so rewriting would attach somebody as
 * head of a group that is now a different group, and picking which of the two wins would be an
 * invention. Those designations are left to stop matching, and the summary says how many.
 */
function rekeyHeadsForOrder(allHeads, order) {
    const rank = new Map(order.map((key, i) => [key, i]));
    const writes = [];
    let moved = 0;
    let stranded = 0;
    for (const [scenarioId, byPerspective] of Object.entries(allHeads)) {
        for (const [perspective, heads] of Object.entries(byPerspective)) {
            for (const [groupId, employeeId] of Object.entries(heads)) {
                if (!groupId.startsWith('G:'))
                    continue;
                const segments = groupId
                    .slice(2)
                    .split('||')
                    .map((segment) => {
                    const eq = segment.indexOf('=');
                    return eq < 0 ? null : { levelKey: segment.slice(0, eq), value: segment.slice(eq + 1) };
                });
                if (segments.some((seg) => seg === null))
                    continue;
                const parsed = segments;
                // A segment naming a level that no longer exists is the removal case, not the reorder case.
                if (parsed.some((seg) => !rank.has(seg.levelKey))) {
                    stranded++;
                    continue;
                }
                const sorted = [...parsed].sort((a, b) => rank.get(a.levelKey) - rank.get(b.levelKey));
                const newId = 'G:' + sorted.map((seg) => `${seg.levelKey}=${seg.value}`).join('||');
                if (newId === groupId)
                    continue;
                writes.push({
                    t: 'head',
                    scenarioId,
                    perspective: perspective,
                    groupId,
                    employeeId: null,
                    present: false,
                });
                writes.push({
                    t: 'head',
                    scenarioId,
                    perspective: perspective,
                    groupId: newId,
                    employeeId,
                    present: true,
                });
                moved++;
            }
        }
    }
    return { writes, moved, stranded };
}
export const setGroupingLevels = {
    needs: ['settings', 'project', 'allHeads'],
    apply(slice, command) {
        const project = required(slice, 'project');
        const settings = required(slice, 'settings');
        const seen = new Set();
        for (const level of command.levels) {
            if (seen.has(level.key))
                throw new CommandError('invalid', `Duplicate level key ${level.key}`);
            seen.add(level.key);
            if (!settings.columnMap[level.key]) {
                throw new CommandError('conflict', `Level ${level.key} has no mapped column`);
            }
        }
        // Reusing a key a removed level once held is the original's `nextLevelKey` bug: the key is not
        // re-keyed, but its MEANING is recycled, so the new level silently inherits the old one's head
        // designations, overrides and every employee's stale value. Refusing a key that is not already in
        // use makes the caller pick a fresh one.
        const known = new Set(project.groupingLevels.map((l) => l.key));
        const reused = command.levels.filter((l) => !known.has(l.key) && /^lvl\d+$/.test(l.key));
        const highest = project.groupingLevels.reduce((max, l) => Math.max(max, Number(/^lvl(\d+)$/.exec(l.key)?.[1] ?? 0)), 0);
        for (const level of reused) {
            const n = Number(/^lvl(\d+)$/.exec(level.key)?.[1] ?? 0);
            if (n <= highest) {
                throw new CommandError('conflict', `Level key ${level.key} was used before; use lvl${highest + 1} or higher so it inherits nothing`);
            }
        }
        const removed = project.groupingLevels.filter((l) => !seen.has(l.key));
        const rekeyed = rekeyHeadsForOrder(required(slice, 'allHeads'), command.levels.map((l) => l.key));
        return {
            writes: [{ t: 'project', patch: { groupingLevels: command.levels } }, ...rekeyed.writes],
            inverse: [{ type: 'setGroupingLevels', levels: project.groupingLevels }],
            summary: `Hierarchy levels: ${command.levels.map((l) => l.label).join(' › ') || '(none)'}` +
                (removed.length ? ` — removed ${removed.map((l) => l.label).join(', ')}` : '') +
                (rekeyed.moved ? ` — moved ${rekeyed.moved} head designation(s) onto the reordered path` : '') +
                (rekeyed.stranded
                    ? ` — ${rekeyed.stranded} designation(s) name a removed level and no longer apply`
                    : ''),
            touched: ['groupingLevels'],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Project and notes
 * ------------------------------------------------------------------ */
export const renameProject = {
    needs: ['project'],
    apply(slice, command) {
        const project = required(slice, 'project');
        return {
            writes: [{ t: 'project', patch: { name: command.name } }],
            inverse: [{ type: 'renameProject', name: project.name }],
            summary: `Renamed the project "${project.name}" → "${command.name}"`,
            touched: ['project:name'],
        };
    },
};
export const archiveProject = {
    needs: ['project'],
    apply(slice, command) {
        const project = required(slice, 'project');
        if (project.archived === command.archived) {
            throw new CommandError('conflict', command.archived ? 'The project is already archived' : 'The project is not archived');
        }
        return {
            writes: [{ t: 'project', patch: { archived: command.archived } }],
            inverse: [{ type: 'archiveProject', archived: project.archived }],
            // Reversible and non-destructive, unlike deletion — which is not a command at all.
            summary: command.archived ? 'Archived the project' : 'Restored the project from the archive',
            touched: ['project:archived'],
        };
    },
};
/**
 * A standalone entry in the history: the workshop decision behind a set of changes.
 *
 * No writes at all — the event IS the note. Worth stating, because a handler that returns no writes
 * looks like an oversight until you see that the audit trail is the deliverable.
 */
export const addNote = {
    needs: [],
    apply(_slice, command) {
        return {
            writes: [],
            /*
              An EMPTY inverse, not a null one, and the difference matters when a batch is reverted.
      
              `null` means "this cannot be undone" — deleting a scenario takes a whole design with it. `[]` means
              "there is nothing to undo", which is the truth about a note: removing it from the history would be
              rewriting the history, and the log is append-only. Conflating the two made any batch containing a
              note unrevertable, because one note poisoned the whole group.
            */
            inverse: [],
            summary: command.note,
            touched: [],
        };
    },
};
/**
 * The record that a SteerCo pack was issued — the act itself happens around this event.
 *
 * No writes, like `addNote`: the design is exactly what it was, and that is the point — the pack is
 * a photograph, and a photograph that changed its subject would prove nothing. What the event buys
 * is coordinates: the restore point stamped immediately before it (`SNAPSHOT_BEFORE` names this
 * command explicitly, in `apps/server/src/commands.ts`) is the state the files were rendered from,
 * and the events between two of these ARE "what moved since the last SteerCo".
 *
 * `inverse: []`, LIKE `addNote` AND FOR A LOAD-BEARING REASON, though the first draft argued for
 * `null` ("a pack cannot be un-issued"). The revert guards read `inverse: null` as "writes with no
 * way back", so a later null-inverse event flips every earlier destructive undo to
 * stale-destructive — and a pack, which writes nothing, would have frozen undos across the whole
 * project the moment it was issued, with a refusal naming a change that built nothing. `[]` is the
 * marker whose invariant this event actually satisfies: there is nothing here to undo, and the
 * files being out of reach is a fact about the world, not about the log.
 */
export const issueSteercoPack = {
    needs: [],
    apply(_slice, command) {
        return {
            writes: [],
            inverse: [],
            summary: `SteerCo pack "${command.name}" issued`,
            touched: [],
        };
    },
};
export const annotateEvent = {
    /*
      The one thing this command has to be sure of: that the event it names is this project's.
  
      It declared `needs: []` and wrote straight through. `event_annotations` carries a foreign key onto
      `change_events (id)` and a separate `project_id` taken from the caller's own context, so the two were
      never checked against each other — and the id comes verbatim from the request body. An id belonging to
      ANOTHER project satisfied the constraint: the row was stored under the caller's project, pointing at a
      change in somebody else's, and would have been cascaded away by a deletion they could not see. An id
      belonging to nobody raised a constraint violation the API's error handler has no branch for, so the
      client was told "Internal server error" about a typo.
    */
    needs: ['event'],
    apply(slice, command) {
        const event = slice.event;
        if (event === undefined) {
            throw new Error('Command slice "event" was not loaded — check the handler\'s needs declaration');
        }
        if (event === null) {
            throw new CommandError('not_found', `No change "${command.eventId}" in this project, so there is nothing to annotate`);
        }
        return {
            // A projection rather than a column on change_events, because that table is append-only: the
            // annotation is its own event and this is its read model.
            writes: [{ t: 'annotation', eventId: command.eventId, note: command.note }],
            // Nothing to undo rather than impossible to undo — see addNote above. An annotation is a remark about
            // a change, and reverting the change should not silently retract what somebody said about it.
            inverse: [],
            summary: command.note ? `Noted against an earlier change: ${command.note}` : 'Removed a note',
            touched: [`annotation:${command.eventId}`],
        };
    },
};
//# sourceMappingURL=settings.js.map